/**
 * Cart Drawer Upsell — SwiperJS initialization and add-to-cart handling.
 *
 * Initializes the "Pairs well with" carousel and handles upsell add-to-cart
 * by posting to the Shopify Cart API and dispatching cart:update to trigger
 * cart drawer morphing.
 *
 * Handles reinit after cart morphing (DOM replacement) and deferred Swiper loading.
 */

let swiperInstance = null;

function initUpsellSwiper() {
  if (swiperInstance) {
    swiperInstance.destroy(true, true);
    swiperInstance = null;
  }

  const container = document.querySelector('.maison-upsell__swiper');
  if (!container || typeof Swiper === 'undefined') return;

  // Don't init if the dialog is closed — Swiper needs accurate dimensions
  const dialog = container.closest('dialog');
  if (dialog && !dialog.open) return;

  const section = container.closest('[data-upsell-section]');
  if (!section) return;

  const prevBtn = section.querySelector('.maison-upsell__nav-btn--prev');
  const nextBtn = section.querySelector('.maison-upsell__nav-btn--next');
  const pagination = section.querySelector('.maison-upsell__pagination');

  swiperInstance = new Swiper(container, {
    slidesPerView: 'auto',
    spaceBetween: 12,
    observer: true,
    observeParents: true,
    navigation: {
      prevEl: prevBtn,
      nextEl: nextBtn,
    },
    pagination: {
      el: pagination,
      clickable: true,
      renderBullet(index, className) {
        return `<span class="${className} maison-upsell__bullet"></span>`;
      },
    },
    on: {
      init(swiper) {
        updateNavState(swiper, prevBtn, nextBtn);
      },
      slideChange(swiper) {
        updateNavState(swiper, prevBtn, nextBtn);
      },
    },
  });
}

/**
 * Disable prev/next buttons at boundaries.
 */
function updateNavState(swiper, prevBtn, nextBtn) {
  if (!prevBtn || !nextBtn) return;
  prevBtn.disabled = swiper.isBeginning;
  nextBtn.disabled = swiper.isEnd;
}

/**
 * Handle upsell "Add +" button clicks via event delegation.
 */
function initAddToCart() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.maison-upsell__add-btn');
    if (!btn || btn.disabled || btn.dataset.loading === 'true') return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.dataset.loading = 'true';

    try {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = [];
      cartItemsComponents.forEach((el) => {
        if (el.dataset.sectionId) {
          sectionIds.push(el.dataset.sectionId);
        }
      });

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });

      const data = await response.json();

      if (data.status) {
        console.error('Upsell add-to-cart error:', data.description);
        return;
      }

      // Dispatch cart:update so cart-items-component morphs the drawer
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            data: {
              source: 'cart-drawer-upsell',
              sections: data.sections,
            },
          },
        })
      );
    } catch (error) {
      console.error('Upsell add-to-cart failed:', error);
    } finally {
      btn.dataset.loading = 'false';
    }
  });
}

/**
 * Reinit Swiper after cart morph replaces the DOM.
 */
function listenForCartUpdates() {
  document.addEventListener('cart:update', () => {
    // Morph happens async — wait for DOM to settle before reinit
    setTimeout(() => waitForSwiper(initUpsellSwiper), 500);
  });
}

/**
 * Init Swiper when the cart drawer dialog opens (not on page load when dialog is hidden).
 */
function listenForDialogOpen() {
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-testid="cart-drawer-trigger"]')) {
      setTimeout(() => waitForSwiper(initUpsellSwiper), 100);
    }
  });
}

function waitForSwiper(callback, attempts = 0) {
  if (typeof Swiper !== 'undefined') {
    callback();
    return;
  }
  if (attempts > 100) return;
  setTimeout(() => waitForSwiper(callback, attempts + 1), 50);
}

function init() {
  waitForSwiper(initUpsellSwiper);
  initAddToCart();
  listenForCartUpdates();
  listenForDialogOpen();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
